import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PositiveGoalCard: React.FC = () => {
  const navigate = useNavigate();

  return (
    <section
      role="region"
      aria-labelledby="goal-title"
      className="relative overflow-hidden rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-teal-50 to-blue-50 p-6 md:p-8 shadow-xl"
    >
      {/* soft decorative blobs */}
      <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-emerald-300/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-blue-300/40 blur-3xl" />

      <header className="mb-6">
        <h2 id="goal-title" className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900">
          Our Goal: Keep More of Your Uber Money 💸
        </h2>
        <p className="mt-2 text-sm md:text-base text-gray-700 font-medium">
          Built for Ontario, British Columbia, Alberta and Manitoba sole proprietors on GST/HST:
          simple capture, clear ITCs, audit-ready exports.
        </p>
      </header>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <p className="text-base md:text-lg leading-relaxed text-gray-800">
            AutoTaxPilot helps Uber drivers{' '}
            <span className="font-bold text-emerald-700">reduce taxes</span> by capturing what's
            deductible and maximizing{' '}
            <span className="font-bold text-blue-700">ITCs (Input Tax Credits)</span>. We know rides
            are busy and receipts get lost then tax time hits and it hurts. We're here to change
            that: <span className="font-bold text-indigo-700">log fast, file confidently</span>, and
            keep more in your wallet.
          </p>

          <ul className="mt-4 space-y-3 text-sm md:text-base text-gray-700">
            <li className="flex items-start">
              <span className="text-emerald-600 mr-2 text-lg">✅</span>
              <span>
                <span className="font-bold text-gray-900">Receipts handled:</span> keep clean
                digital copies CRA accepts physical <em>or</em> digital.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-emerald-600 mr-2 text-lg">✅</span>
              <span>
                <span className="font-bold text-gray-900">Audit-ready:</span> retained ≥ 6 years
                with file hashes and immutable exports.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-emerald-600 mr-2 text-lg">✅</span>
              <span>
                <span className="font-bold text-gray-900">ITC clarity:</span> fuel, maintenance,
                accessories, parking/tolls; partial phone; meals/entertainment (50% rule if
                qualified).
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-emerald-600 mr-2 text-lg">✅</span>
              <span>
                <span className="font-bold text-gray-900">One-click CRA mapping:</span> exact vs
                CRA-rounded, lines 90–115.
              </span>
            </li>
            <li className="flex items-start">
              <span className="text-emerald-600 mr-2 text-lg">✅</span>
              <span>
                <span className="font-bold text-gray-900">Your data, your control:</span> owner-only
                access rules; no selling or sharing your data.
              </span>
            </li>
          </ul>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-gray-700 p-6 shadow-xl">
            <h3 className="font-bold text-xl text-white mb-3">Why this matters</h3>
            <p className="text-base md:text-lg text-gray-100 leading-relaxed">
              Annual filing? We forget the most. Monthly/quarterly? It's still easy to slip. This
              system is built to be <span className="font-bold text-emerald-300">worry-free</span>:
              capture in seconds, file with a click, and stay compliant. If CRA audits, you're
              covered with organized records.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              data-ref="addExpenseBtn"
              onClick={() => navigate('/expenses?new=1', { replace: false })}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 py-3 text-white font-bold shadow-lg hover:shadow-xl hover:from-emerald-600 hover:to-emerald-700 transition-all"
            >
              Add an Expense Now <ArrowRight className="h-5 w-5" />
            </button>
            <button
              data-ref="startSessionBtn"
              onClick={() => navigate('/sessions?start=1', { replace: false })}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 px-5 py-3 text-white font-bold shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-700 transition-all"
            >
              Start Driving Session <ArrowRight className="h-5 w-5" />
            </button>
          </div>

          <p className="text-xs md:text-sm text-gray-600 bg-white/60 rounded-lg p-3 border border-gray-200">
            When you're ready to file CRA as a Business [beta: TurboTax as an T1_T2125], generate
            your report no second-guessing boxes or lines. We'll surface the exact values and their
            CRA line mapping.
          </p>
        </div>
      </div>

      <footer className="mt-6 text-sm md:text-base text-gray-700 text-center border-t border-gray-200 pt-4">
        Thanks for trusting <span className="font-bold text-blue-700">AutoTaxPilot</span> — built to
        be <span className="font-bold text-emerald-700">peace-of-mind</span> for Uber taxes.
      </footer>
    </section>
  );
};

export default PositiveGoalCard;
